/**
 * services/orderService.ts - Order API Service
 */

import apiClient from './apiClient';

export interface OrderItem {
  menuItemId: string;
  name: string;
  price: number;
  quantity: number;
}

export interface Order {
  id?: string;
  _id?: string;
  orderNumber: string;
  vendorId: string;
  vendorName: string;
  customerId?: string;
  customerName: string;
  items: OrderItem[];
  totalAmount: number;
  status: 'pending' | 'confirmed' | 'preparing' | 'ready' | 'out-for-delivery' | 'on-delivery' | 'delivered' | 'cancelled';
  seatLocation?: {
    section: string;
    row: string;
    seat: string;
  };
  deliveryInstructions?: string;
  createdAt?: string;
  placedAt?: string;
  completedAt?: string;
  confirmedAt?: string;
  preparingAt?: string;
  readyAt?: string;
  onDeliveryAt?: string;
  deliveredAt?: string;
  pendingToPreparingMs?: number;
  preparingToReadyMs?: number;
  readyToOnDeliveryMs?: number;
  onDeliveryToDeliveredMs?: number;
  estimatedDeliveryTime?: number;
  assignedTo?: string;
  eventId?: string;
}

class OrderService {
  private basePath = '/orders';

  async getAll(params?: { status?: string; vendorId?: string; limit?: number }): Promise<Order[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.status) queryParams.append('status', params.status);
      if (params?.vendorId) queryParams.append('vendorId', params.vendorId);
      if (params?.limit) queryParams.append('limit', params.limit.toString());

      const url = queryParams.toString() 
        ? `${this.basePath}?${queryParams.toString()}`
        : this.basePath;

      return await apiClient.get<Order[]>(url);
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw error;
    }
  }

  async getById(id: string): Promise<Order> {
    try {
      return await apiClient.get<Order>(`${this.basePath}/${id}`);
    } catch (error) {
      console.error('Error fetching order:', error);
      throw error;
    }
  }

  async create(orderData: Omit<Order, '_id' | 'orderNumber'>): Promise<Order> {
    try {
      return await apiClient.post<Order>(this.basePath, orderData);
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  }

  async updateStatus(id: string, status: Order['status'], assignedTo?: string): Promise<Order> {
    try {
      return await apiClient.put<Order>(`${this.basePath}/${id}/status`, {
        status,
        assignedTo
      });
    } catch (error) {
      console.error('Error updating order status:', error);
      throw error;
    }
  }

  async delete(id: string): Promise<void> {
    try {
      await apiClient.delete(`${this.basePath}/${id}`);
    } catch (error) {
      console.error('Error deleting order:', error);
      throw error;
    }
  }

  async exportCsv(params?: { vendorId?: string; startDate?: string; endDate?: string }): Promise<Blob> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.vendorId) queryParams.append('vendorId', params.vendorId);
      if (params?.startDate) queryParams.append('startDate', params.startDate);
      if (params?.endDate) queryParams.append('endDate', params.endDate);
      queryParams.append('format', 'csv');
      const url = `${this.basePath}/export?${queryParams.toString()}`;
      return await apiClient.getBlob(url);
    } catch (error) {
      console.error('Error exporting orders CSV:', error);
      throw error;
    }
  }

  async bulkUpdateStatus(orderIds: string[], status: Order['status']): Promise<Order[]> {
    try {
      const result = await apiClient.put<{ orders: Order[] }>(`${this.basePath}/bulk-status`, { orderIds, status });
      return result.orders ?? [];
    } catch (error) {
      console.error('Error bulk updating order status:', error);
      throw error;
    }
  }

  async getStats(params?: { vendorId?: string; startDate?: string; endDate?: string }): Promise<any> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.vendorId) queryParams.append('vendorId', params.vendorId);
      if (params?.startDate) queryParams.append('startDate', params.startDate);
      if (params?.endDate) queryParams.append('endDate', params.endDate);

      const url = queryParams.toString()
        ? `${this.basePath}/stats/summary?${queryParams.toString()}`
        : `${this.basePath}/stats/summary`;

      return await apiClient.get(url);
    } catch (error) {
      console.error('Error fetching order stats:', error);
      throw error;
    }
  }
}

const orderService = new OrderService();
export default orderService;
